// Glass — astral / deep-space glassmorphism. The original look and the
// default theme. Translucent frosted panels over a near-black navy with
// a desaturated steel-blue accent family.
//
// Every value here is the source of truth; buildCss.ts compiles it into
// the --* custom properties used across the app.

import type { ThemeDefinition } from "./types";
import { baseRadius, baseTypography } from "./shared";

export const glass: ThemeDefinition = {
  name: "glass",
  label: "Glass",
  description: "Frosted glass panels on deep navy, with steel-blue accents.",
  mode: "dark",
  browserChrome: "#0a0a0c",

  // A link is marked by more than its hue: a quiet underline (index.css).
  typography: { ...baseTypography, linkUnderline: true },
  radius: baseRadius,

  color: {
    ink: "255, 255, 255",
    inkSpace: "255 255 255",
    accentRgb: "74, 111, 165",

    bg: "#050810",
    surface0: "#04060d",
    surface1: "#080d1a",
    surface2: "#0a0f1e",
    surface3: "#141b3d",

    textPrimary: "#f8fafc",
    textMuted: "rgba(255, 255, 255, 0.75)",
    // faint/ghost clear AA (>= 4.5:1) on bg, panel and raised.
    textFaint: "rgba(255, 255, 255, 0.56)",
    textGhost: "rgba(255, 255, 255, 0.5)",

    // `accent` is the FILL (white on it 5.1:1); `accentLight` is the
    // accent used as text / links / active tab (AA on every surface).
    accent: "#4a6fa5",
    accentLight: "#8eaadb",
    accentDark: "#3a5580",
    accentDeeper: "#1e2e4a",
    onAccent: "#ffffff",

    success: "#3dbb6e",
    warning: "#d9a83a",
    danger: "#e16d7b", // AA as text on raised and on its own /15 tint
    info: "#8eaadb",
    dangerFill: "#b3404e",
    focus: "#8eaadb",

    raised: "rgba(255, 255, 255, 0.06)",
    glassPanel: "rgba(255, 255, 255, 0.02)",
    glassTile: "rgba(255, 255, 255, 0.02)",
    glassKpi: "rgba(255, 255, 255, 0.02)",
    glassInset: "rgba(0, 0, 0, 0.25)",
    glassButton: "rgba(255, 255, 255, 0.04)",
    glassButtonHover: "rgba(255, 255, 255, 0.08)",

    activeAccent: "rgba(74, 111, 165, 0.15)",
    activeAccentBorder: "rgba(74, 111, 165, 0.2)",
    activeAccentStrong: "rgba(74, 111, 165, 0.3)",
    // web only: input/secondary-button edge, selected-row fill
    controlEdge: "rgba(255, 255, 255, 0.2)",
    selected: "rgba(74, 111, 165, 0.26)",

    avatarBg: "rgba(74, 111, 165, 0.2)",
    avatarBorder: "rgba(74, 111, 165, 0.2)",
    avatarText: "#c8d4ea",
    sidebarBg: "rgba(0, 0, 0, 0.2)",
    sidebarEdge: "rgba(255, 255, 255, 0.03)",
    brandBadge: "#1f3464",
    brandBadgeMuted: "#161a26",
    canvasGrid: "#1c1c1f",

    selectionBg: "rgba(74, 111, 165, 0.32)",
    selectionText: "#f8fafc",
    scrim: "rgba(2, 6, 14, 0.64)",
    modalPanel: "rgba(8, 13, 26, 0.74)",
    overlay: "#0d1324",
    tabbarBg: "#0a0f1e",
  },

  contrast: {
    border: {
      hairline: "rgba(255, 255, 255, 0.03)",
      subtle: "rgba(255, 255, 255, 0.05)",
      visible: "rgba(255, 255, 255, 0.08)",
      strong: "rgba(255, 255, 255, 0.12)",
    },
    bevel: {
      top: "rgba(255, 255, 255, 0.05)",
      topBright: "rgba(255, 255, 255, 0.08)",
      topStrong: "rgba(255, 255, 255, 0.12)",
      bottom: "rgba(0, 0, 0, 0.3)",
      bottomDark: "rgba(0, 0, 0, 0.4)",
    },
  },

  material: {
    flat: false,
    blur: { lg: "blur(20px)", md: "blur(12px)", sm: "blur(8px)" },
    shadow: {
      panel: "0 2px 16px rgba(0, 0, 0, 0.3)",
      tile: "0 2px 12px rgba(0, 0, 0, 0.25)",
      subtle: "0 1px 6px rgba(0, 0, 0, 0.2)",
      sidebarEdge: "1px 0 0 rgba(255, 255, 255, 0.03)",
      sidebarCast: "8px 0 32px -12px rgba(0, 0, 0, 0.5)",
      accentHalo: "0 0 16px -4px rgba(74, 111, 165, 0.25)",
      accentHaloStrong: "0 0 18px -4px rgba(74, 111, 165, 0.35)",
    },
  },

  ambient: [
    "radial-gradient(ellipse 600px 400px at 80% 10%, rgba(56, 82, 130, 0.08), transparent)",
    "radial-gradient(ellipse 500px 500px at 20% 85%, rgba(30, 58, 110, 0.05), transparent)",
    "radial-gradient(ellipse 900px 300px at 50% 30%, rgba(40, 60, 100, 0.03), transparent)",
    "linear-gradient(160deg, #050810 0%, #0a0f1e 35%, #080d1a 65%, #04060d 100%)",
  ].join(", "),

  scales: {
    surface: {
      "50": "250 250 250",
      "100": "244 244 245",
      "200": "228 228 231",
      "300": "161 161 170",
      // 400-600 are TEXT greys (meta, placeholders): lifted to clear AA.
      "400": "152 152 164",
      "500": "146 146 158",
      // 600 aliases 500: three AA levels is all a 4.5:1 floor leaves room
      // for, so 600 stays only for existing call sites.
      "600": "146 146 158",
      "700": "39 39 42",
      "800": "28 28 31",
      "880": "21 21 26",
      "900": "17 17 20",
      "950": "10 10 12",
    },
    accent: {
      "50": "238 242 248",
      "100": "212 223 239",
      "200": "168 192 223",
      // 300/400 are accent-as-TEXT (light); 500 is the fill; 600 is the
      // darker primary HOVER fill (white on it 7.5:1).
      "300": "142 170 219",
      "400": "118 150 198",
      "500": "74 111 165",
      "600": "58 85 128",
      "700": "46 68 101",
      "800": "37 54 79",
      "900": "30 46 74",
    },
  },

  extended: {
    accentSoftRgb: "80, 120, 200",
    accentLightRgb: "107, 135, 184",
    accentInfo2Rgb: "160, 184, 232",
    accentSkyRgb: "120, 180, 220",
    accentGlowRgb: "120, 160, 230",
    accentGlowDeepRgb: "80, 110, 200",
    researchRgb: "170, 110, 220",
    catTealRgb: "125, 195, 168",
    accentInfo: "#79c0ff",
    accentInfo2: "#a0b8e8",
    catViolet: "#c58ad3",
    catSteel: "#9bb0d6",
    // cool-1 / cw-slate(-2) are used as TEXT (sidebar meta, inactive nav,
    // done / cancelled): AA on bg, panel, tile and raised. Ladder stays
    // ordered dim -> bright (cool-1 < cool-2 < cool-3 < cool-4).
    cool1: "#868fa4",
    cool2: "#8894ad",
    cool3: "#8a97b0",
    cool4: "#a8b1c0",
    cwSlate: "#858c9f",
    cwSlate2: "#898f9c",
    cwBlue: "#86a8d8",
    cwBlueL: "#a0c4f0",
    cwBlueXl: "#c0d4f8",
    cwViolet: "#b79ad6",
    cwVioletL: "#c9a4e8",
    cwVioletD: "#a87dc3",
    orgnodeRgb: "14, 18, 32",
    orgnodeSelRgb: "30, 42, 80",
    coolShadowRgb: "0, 4, 20",
    coolOverlayRgb: "10, 15, 30",
  },

  js: {
    canvasGrid: "#1c1c1f",
    canvasMask: "rgba(10, 10, 12, 0.8)",
    edgeStroke: "#8eaadb",
    edgeRgb: "140, 170, 230",
    teamColours: [
      "rgba(130, 170, 250, 0.55)",
      "rgba(200, 150, 240, 0.55)",
      "rgba(140, 210, 200, 0.55)",
    ],
    avatarHues: "wheel",
    avatarBg: [35, 25],
    avatarFg: [50, 75],
  },
};
